import { useState, useEffect } from "react";
import axios from "axios";
import { ChevronRight } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StageDisplay from "./tuning/StageDisplay";
import EcuSection from "./tuning/EcuSection";
import ExtraOptions, { useExtraOptions } from "./tuning/ExtraOptions";
import QuoteForm from "./tuning/QuoteForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PowerGainsSection() {
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [generations, setGenerations] = useState([]);
  const [engines, setEngines] = useState([]);
  const [selected, setSelected] = useState({ brand: "", model: "", generation: "" });
  const [stage, setStage] = useState("1");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const { selectedExtras, filteredOptions, toggleExtra, resetExtras } = useExtraOptions(results?.fuel_type);

  useEffect(() => {
    axios.get(`${API}/tuning/brands`).then((r) => setBrands(r.data)).catch(() => {});
  }, []);

  const fetchEngines = async (brand, model, generation, stageVal) => {
    if (!brand || !model || !generation) return;
    setLoading(true);
    setResults(null);
    const r = await axios.get(`${API}/tuning/engines`, { params: { brand, model, generation, stage: stageVal } });
    setEngines(r.data);
    setLoading(false);
  };

  const handleBrandChange = async (val) => {
    setSelected({ brand: val, model: "", generation: "" });
    setModels([]); setGenerations([]); setEngines([]); setResults(null);
    if (!val) return;
    const r = await axios.get(`${API}/tuning/models`, { params: { brand: val } });
    setModels(r.data);
  };

  const handleModelChange = async (val) => {
    setSelected((p) => ({ ...p, model: val, generation: "" }));
    setGenerations([]); setEngines([]); setResults(null);
    if (!val) return;
    const r = await axios.get(`${API}/tuning/generations`, { params: { brand: selected.brand, model: val } });
    setGenerations(r.data);
  };

  const handleGenChange = async (val) => {
    setSelected((p) => ({ ...p, generation: val }));
    setEngines([]); setResults(null);
    if (!val) return;
    fetchEngines(selected.brand, selected.model, val, stage);
  };

  const handleStageChange = (val) => {
    setStage(val); setResults(null); resetExtras();
    if (selected.generation) {
      const apiStage = val === "3" || val === "stock" ? "1" : val;
      fetchEngines(selected.brand, selected.model, selected.generation, apiStage);
    }
  };

  const showResult = (engine) => { setResults(engine); resetExtras(); };

  const stageBadgeClass = stage === "3"
    ? "bg-[#9B59B6]/20 text-[#9B59B6] border border-[#9B59B6]/30"
    : stage === "2"
      ? "bg-[#FF3B30]/20 text-[#FF3B30] border border-[#FF3B30]/30"
      : stage === "stock"
        ? "bg-[#2A2A2A] text-[#8A8A8A]"
        : "bg-[#2A2A2A] text-white";

  return (
    <section id="power-gains" data-testid="power-gains-section" className="py-24 px-4 bg-[#111111]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="section-title text-4xl sm:text-5xl text-white mb-4">Performance Lookup</h2>
          <p className="text-[#8A8A8A] mt-8 max-w-2xl mx-auto">Select your vehicle and tuning stage to see potential power and torque gains</p>
        </div>

        {/* Search Dropdowns */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div>
            <label className="block text-xs text-[#8A8A8A] uppercase tracking-wider mb-2">Brand</label>
            <Select value={selected.brand} onValueChange={handleBrandChange}>
              <SelectTrigger data-testid="tuning-brand-select" className="bg-[#1A1A1A] border-[#2A2A2A] text-white h-12"><SelectValue placeholder="Select brand" /></SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A] max-h-60">{brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs text-[#8A8A8A] uppercase tracking-wider mb-2">Model</label>
            <Select value={selected.model} onValueChange={handleModelChange} disabled={!models.length}>
              <SelectTrigger data-testid="tuning-model-select" className="bg-[#1A1A1A] border-[#2A2A2A] text-white h-12"><SelectValue placeholder="Select model" /></SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A] max-h-60">{models.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs text-[#8A8A8A] uppercase tracking-wider mb-2">Generation</label>
            <Select value={selected.generation} onValueChange={handleGenChange} disabled={!generations.length}>
              <SelectTrigger data-testid="tuning-gen-select" className="bg-[#1A1A1A] border-[#2A2A2A] text-white h-12"><SelectValue placeholder="Select generation" /></SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A] max-h-60">{generations.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs text-[#8A8A8A] uppercase tracking-wider mb-2">Tuning Stage</label>
            <Select value={stage} onValueChange={handleStageChange}>
              <SelectTrigger data-testid="tuning-stage-select" className="bg-[#1A1A1A] border-[#2A2A2A] text-white h-12"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                <SelectItem value="1">Stage 1 — Software only</SelectItem>
                <SelectItem value="2">Stage 2 — Hardware upgrades</SelectItem>
                <SelectItem value="3">Stage 3 — Custom tune</SelectItem>
                <SelectItem value="stock">Back to Stock</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Engine List */}
        {engines.length > 0 && (
          <div data-testid="engine-list" className="mb-8">
            <label className="block text-xs text-[#8A8A8A] uppercase tracking-wider mb-3">Select Engine</label>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {engines.map((e) => (
                <button key={e.slug} data-testid={`engine-${e.slug}`} onClick={() => showResult(e)}
                  className={`text-left p-4 rounded-lg border transition-all ${results?.slug === e.slug ? "bg-[#FF3B30]/10 border-[#FF3B30] text-white" : "bg-[#1A1A1A] border-[#2A2A2A] text-[#B0B0B0] hover:border-[#FF3B30]/50 hover:text-white"}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{e.engine}</span>
                    <ChevronRight size={16} className="text-[#FF3B30]" />
                  </div>
                  {e.fuel_type && <span className="text-[10px] uppercase tracking-wider text-[#8A8A8A] mt-1 inline-block">{e.fuel_type}</span>}
                </button>
              ))}
            </div>
          </div>
        )}

        {loading && <div className="text-center py-8"><div className="spinner mx-auto mb-4" /><p className="text-[#8A8A8A]">Loading engine data...</p></div>}

        {/* Results Display */}
        {results && (
          <div data-testid="tuning-results" className="tuning-results-card rounded-lg p-8 mt-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8">
              <div>
                <h3 className="text-2xl text-white font-semibold">{selected.brand} {selected.model}</h3>
                <p className="text-[#8A8A8A] text-sm mt-1">{selected.generation} &middot; {results.engine}</p>
              </div>
              <div className="flex items-center gap-3 mt-2 sm:mt-0">
                <span className={`px-3 py-1 rounded-full text-xs uppercase tracking-wider font-medium ${stageBadgeClass}`}>
                  {stage === "stock" ? "Back to Stock" : `Stage ${stage}`}
                </span>
                {results.fuel_type && (
                  <span className={`px-3 py-1 rounded-full text-xs uppercase tracking-wider font-medium ${results.fuel_type === "Diesel" ? "bg-[#2A2A2A] text-[#6AB04C]" : "bg-[#2A2A2A] text-[#FF3B30]"}`}>
                    {results.fuel_type}
                  </span>
                )}
              </div>
            </div>

            <StageDisplay stage={stage} results={results} />
            <EcuSection ecus={results.ecus} />
            <ExtraOptions filteredOptions={filteredOptions} selectedExtras={selectedExtras} toggleExtra={toggleExtra} />

            {stage === "2" && <p className="mt-4 text-xs text-[#8A8A8A] text-center italic">Stage 2 gains require hardware upgrades. Actual results vary.</p>}
            {stage === "3" && <p className="mt-4 text-xs text-[#8A8A8A] text-center italic">Stage 3 is a fully custom build. Contact us for a detailed consultation.</p>}
            {stage === "stock" && <p className="mt-4 text-xs text-[#8A8A8A] text-center italic">Restore your ECU to the original factory mapping.</p>}

            <QuoteForm selected={selected} results={results} stage={stage} selectedExtras={selectedExtras} />
          </div>
        )}
      </div>
    </section>
  );
}
